import { useEffect, useState } from "react";
import {
  getMarketingConsentStatus,
  MARKETING_CONSENT_STATUS,
} from "../api/customerPreferencesApi";
import { getLoyaltySDK } from "../api/loyaltySdk";
import { CUSTOMER_ACCESS_PURCHASE, LOYALTY_CONSENT_STATUS } from "../constants";
import { getCustomerIsAuthenticated } from "../utils/identity";
import { logError, ERROR_TYPE } from "../utils/newRelicLogger";

export const CUSTOMER_STATE = {
  LOGGED_OUT: "logged-out customer",
  LOYALTY_NONE_OPTED_IN_MARKETING: "non-loyalty member, opted-in to marketing",
  LOYALTY_NONE_OPTED_OUT_MARKETING:
    "non-loyalty member, opted-out of marketing",
  LOYALTY_VIEW_OPTED_IN_MARKETING:
    "loyalty member, can view early access, opted-in to marketing",
  LOYALTY_VIEW_OPTED_OUT_MARKETING:
    "loyalty member, can view early access, opted-out of marketing",
  LOYALTY_PURCHASE: "loyalty member, can purchase early access",
} as const;

export function useEarlyAccessCustomerState(customerAccess: string | null) {
  const [customerState, setCustomerState] = useState<string | null>(null);

  useEffect(() => {
    const determineCustomerState = async () => {
      try {
        if (customerAccess === CUSTOMER_ACCESS_PURCHASE) {
          setCustomerState(CUSTOMER_STATE.LOYALTY_PURCHASE);
          return;
        }

        const isAuthenticated = await getCustomerIsAuthenticated();
        if (!isAuthenticated) {
          setCustomerState(CUSTOMER_STATE.LOGGED_OUT);
          return;
        }

        const loyaltySdk = await getLoyaltySDK();
        if (!loyaltySdk.isLoyaltyAvailable()) {
          setCustomerState(CUSTOMER_STATE.LOYALTY_VIEW_OPTED_IN_MARKETING);
          return;
        }

        const [customerDetails, consentStatus] = await Promise.all([
          loyaltySdk.getCustomerDetails(),
          getMarketingConsentStatus(),
        ]);
        const optedInToMarketing =
          consentStatus === MARKETING_CONSENT_STATUS.OPTED_IN;

        if (
          customerDetails?.consentStatus !== LOYALTY_CONSENT_STATUS.OPTED_IN
        ) {
          setCustomerState(
            optedInToMarketing
              ? CUSTOMER_STATE.LOYALTY_NONE_OPTED_IN_MARKETING
              : CUSTOMER_STATE.LOYALTY_NONE_OPTED_OUT_MARKETING,
          );
          return;
        }

        if (!optedInToMarketing) {
          setCustomerState(CUSTOMER_STATE.LOYALTY_VIEW_OPTED_OUT_MARKETING);
          return;
        }

        setCustomerState(CUSTOMER_STATE.LOYALTY_VIEW_OPTED_IN_MARKETING);
      } catch (error) {
        logError(ERROR_TYPE.SdkError, {
          action: "determineCustomerState",
          message: (error as Error).message,
        });
        setCustomerState(CUSTOMER_STATE.LOYALTY_VIEW_OPTED_IN_MARKETING);
      }
    };

    determineCustomerState();
  }, [customerAccess]);

  return { customerState, setCustomerState };
}
